import { useState } from 'react'
import {
  Box, Breadcrumbs, Typography, Stack, Paper, Table, TableBody, TableRow, TableCell,
  TextField, Card, CardContent, Alert,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { getKpiReport } from '../../api/kpi'

function firstDayOfMonth(): string {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10)
}
function today(): string {
  return new Date().toISOString().slice(0, 10)
}

const money = (v: number) => v.toFixed(2).replace('.', ',')
const percent = (v: number | null) => (v == null ? '—' : `${(v * 100).toFixed(1).replace('.', ',')}%`)

export default function KpiPage() {
  const [from, setFrom] = useState(firstDayOfMonth())
  const [to, setTo] = useState(today())

  const { data, isLoading, isError } = useQuery({
    queryKey: ['planificare', 'kpi', from, to],
    queryFn: () => getKpiReport(from, to),
    enabled: from <= to,
  })

  return (
    <Stack spacing={2}>
      <Breadcrumbs separator="/" sx={{ fontSize: 12.5 }}>
        <Typography variant="inherit" color="text.secondary">Planificare</Typography>
        <Typography variant="inherit" color="text.primary">KPI și P&amp;L</Typography>
      </Breadcrumbs>
      <Typography variant="h2">KPI și P&amp;L</Typography>

      <Stack direction="row" spacing={2}>
        <TextField label="De la" type="date" size="small" value={from}
          onChange={(e) => setFrom(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
        <TextField label="Până la" type="date" size="small" value={to}
          onChange={(e) => setTo(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
      </Stack>

      {from > to && <Alert severity="warning">Data de final trebuie să fie după data de start.</Alert>}
      {isError && <Alert severity="error">Nu s-a putut încărca raportul.</Alert>}

      {data && (
        <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Cont de profit și pierdere (FR-FPL-001)</Typography>
            <Table size="small">
              <TableBody>
                <PnlRow label="Venituri" value={data.pnl.venituri} />
                <PnlRow label="− Cost materii prime" value={-data.pnl.costMP} />
                <PnlRow label="− Cost conversie" value={-data.pnl.costConversie} />
                <PnlRow label="= Marjă brută" value={data.pnl.marjaBruta} bold />
                <PnlRow label="− Cheltuieli operaționale" value={-data.pnl.cheltuieliOperationale} />
                <PnlRow label="= EBITDA" value={data.pnl.ebitda} bold />
                <PnlRow label="− Amortizare" value={-data.pnl.amortizare} />
                <PnlRow label="= EBIT" value={data.pnl.ebit} bold />
                <PnlRow label="− Cheltuieli financiare" value={-data.pnl.cheltuieliFinanciare}
                  note="nicio infrastructură de dobândă/comision bancar în aplicație — raportat 0, nefabricat" />
                <PnlRow label="= Profit brut" value={data.pnl.profitBrut} bold />
                <PnlRow label="− Impozit (16%)" value={-data.pnl.impozit}
                  note="cotă legală fixă, fără reportare de pierdere — FR-MDT-007 (motor complet) rămâne F2" />
                <PnlRow label="= Profit net" value={data.pnl.profitNet} bold last />
              </TableBody>
            </Table>
          </Paper>

          <Typography variant="overline" color="text.secondary">KPI (FR-FPL-002/003)</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <Stat label="Cifră de afaceri" value={money(data.kpi.ca)} />
            <Stat label="Marjă brută %" value={percent(data.kpi.marjaBrutaPercent)} />
            <Stat label="EBITDA" value={money(data.kpi.ebitda)} />
            <Stat label="Cash-flow operațional" value={money(data.kpi.cashFlowOperational)} />
            <Stat label="Sold creanțe" value={money(data.kpi.soldCreante)} />
            <Stat label="Sold datorii" value={money(data.kpi.soldDatorii)} />
            <Stat label="Valoare stoc" value={money(data.kpi.valoareStoc)} />
            <Stat label="Rotație stoc" value={data.kpi.rotatieStoc.toFixed(2).replace('.', ',')} detail="cost perioadă / stoc la final" />
            <Stat label="OEE — calitate" value={percent(data.kpi.oeeQuality)} />
            <Stat label="OEE — disponibilitate" value={percent(data.kpi.oeeAvailability)} />
            <Stat label="OTIF" value={percent(data.kpi.otifPercent)} />
            <Stat label="Comenzi în perioadă" value={String(data.kpi.nrComenzi)} />
            <Stat label="Rată rebuturi" value={percent(data.kpi.rataRebuturi)} />
            <Stat label="DSO" value={`${data.kpi.dso.toFixed(1).replace('.', ',')} zile`} />
            <Stat label="DPO" value={`${data.kpi.dpo.toFixed(1).replace('.', ',')} zile`} />
          </Box>
        </>
      )}
      {isLoading && <Box sx={{ color: 'text.secondary' }}>Se calculează…</Box>}
    </Stack>
  )
}

function PnlRow({ label, value, bold, last, note }: { label: string; value: number; bold?: boolean; last?: boolean; note?: string }) {
  return (
    <TableRow sx={last ? { '& td': { borderBottom: 'none' } } : undefined}>
      <TableCell sx={{ fontWeight: bold ? 700 : 400 }}>
        {label}
        {note && <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>{note}</Typography>}
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400, fontFamily: '"IBM Plex Mono", monospace' }}>
        {money(value)}
      </TableCell>
    </TableRow>
  )
}

function Stat({ label, value, detail }: { label: string; value: string; detail?: string }) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Typography variant="overline" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ fontFamily: '"IBM Plex Mono", monospace', mt: 0.5 }}>{value}</Typography>
        {detail && <Typography variant="caption" color="text.secondary">{detail}</Typography>}
      </CardContent>
    </Card>
  )
}
